import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import ResultDisplay from '../components/ResultDisplay';
import { subtractTimes, minutesToTime } from '../utils/timeCalculations';
import { getCurrentMonthYear } from '../utils/currentDate';

const SubtracaoPage = () => {
  const [horarioInicial, setHorarioInicial] = useState('08:00');
  const [horarioFinal, setHorarioFinal] = useState('17:00');
  const [turnoNoturno, setTurnoNoturno] = useState(false);
  // Derive the error from the inputs instead of calling setState inside useMemo.
  const calc = useMemo(
    () => subtractTimes(horarioFinal, horarioInicial, turnoNoturno),
    [horarioInicial, horarioFinal, turnoNoturno]
  );
  const error = calc.error || '';
  const result = calc.error
    ? null
    : { formatted: minutesToTime(calc.totalMinutes), decimal: calc.decimal.toFixed(2) };

  const faqs = [
    {
      question: 'Como subtrair horas e minutos?',
      answer: 'Subtraia horas e minutos separadamente. Se os minutos do horário final forem menores, tome 1 hora emprestada (some 60 aos minutos e subtraia 1 das horas). Nossa calculadora faz isso automaticamente.'
    },
    {
      question: 'Como calcular tempo entre dois horários?',
      answer: 'Insira o horário de início e o horário de fim na calculadora. A diferença é mostrada em HH:MM e decimal. Para turnos que cruzam a meia-noite, ative a opção de turno noturno.'
    },
    {
      question: 'Como funciona o cálculo de turno noturno?',
      answer: 'Para turnos que cruzam a meia-noite (ex: 22h às 6h), a calculadora soma 24 horas ao horário final antes de subtrair. Resultado: (6+24) - 22 = 8 horas. A opção "turno noturno" deve ser ativada para esse ajuste automático.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Subtração de Horas e Minutos</title>
        <meta name="description" content="Calcule a diferença entre dois horários de forma rápida e precisa. Subtração de horas e minutos com suporte a turno noturno. Resultado em HH:MM e decimal." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-subtracao-de-horas" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Calculadora de Subtração de Horas e Minutos" />
        <meta property="og:description" content="Calcule a diferença entre dois horários de forma rápida e precisa. Subtração de horas e minutos com suporte a turno noturno. Resultado em HH:MM e decimal." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-subtracao-de-horas" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Subtração de Horas e Minutos" />
        <meta name="twitter:description" content="Calcule a diferença entre dois horários de forma rápida e precisa. Subtração de horas e minutos com suporte a turno noturno. Resultado em HH:MM e decimal." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculadora-de-subtracao-de-horas" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculadora-de-subtracao-de-horas" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Subtração de Horas","item":"https://calculadoradehoras.net.br/calculadora-de-subtracao-de-horas"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Subtração</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Subtração de Horas e Minutos
          </h1>
          <p className="text-lg text-body-text">
            Calcule a diferença entre dois horários com precisão
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Horário Inicial
              </label>
              <input
                type="time"
                value={horarioInicial}
                onChange={(e) => setHorarioInicial(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="horario-inicial"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Horário Final
              </label>
              <input
                type="time"
                value={horarioFinal}
                onChange={(e) => setHorarioFinal(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="horario-final"
              />
            </div>

            <label className="flex items-center p-4 bg-light-bg rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={turnoNoturno}
                onChange={(e) => setTurnoNoturno(e.target.checked)}
                className="mr-3 w-5 h-5 text-blue-primary rounded"
                data-testid="turno-noturno-checkbox"
              />
              <div>
                <div className="font-semibold text-dark-text">Turno Noturno (cruza meia-noite)</div>
                <div className="text-sm text-body-text">Ative se o horário final é do dia seguinte (ex: 22:00 às 06:00)</div>
              </div>
            </label>

            {error && (
              <div className="p-4 bg-red-50 border border-warning-red rounded-lg text-warning-red">
                {error}
              </div>
            )}
          </div>

          {result && (
            <div className="mt-6">
              <ResultDisplay 
                result={result} 
                label="Diferença"
                calculatorName="Subtração de Horas"
                pageUrl="https://calculadoradehoras.net.br/calculadora-de-subtracao-de-horas"
              />
            </div>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Subtrair Horas e Minutos</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A subtração de horas é uma das operações mais comuns no cálculo de jornada de trabalho. Toda vez que você precisa saber quanto tempo se passou entre dois horários, você está fazendo uma subtração de horas. Nossa calculadora simplifica esse processo.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-body-text">
              <li>Insira o <strong>horário inicial</strong> (o mais cedo) no primeiro campo.</li>
              <li>Insira o <strong>horário final</strong> (o mais tarde) no segundo campo.</li>
              <li>Se o seu turno cruza a meia-noite (por exemplo, das 22:00 às 06:00), ative a opção <strong>"Turno noturno"</strong> para que o cálculo considere a virada do dia.</li>
            </ol>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Cálculo de Turno Noturno (Cruzando a Meia-Noite)</h2>
            <p className="text-body-text leading-relaxed mb-4">
              Quando o horário de saída é menor que o horário de entrada (por exemplo, entrada às 22:00 e saída às 06:00), o cálculo precisa considerar que o dia mudou. Sem essa correção, a subtração simples daria um resultado negativo.
            </p>
            <p className="text-body-text leading-relaxed">
              <strong>Como funciona:</strong> A calculadora soma 24 horas ao horário de saída antes de subtrair. Exemplo: (06:00 + 24:00) - 22:00 = 30:00 - 22:00 = <strong>8 horas</strong>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Exemplos Práticos</h2>
            
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 1: Jornada diurna simples</h3>
              <p className="text-body-text leading-relaxed">
                Entrada: 08:30. Saída: 17:15.<br/>
                <strong>Resultado:</strong> 8 horas e 45 minutos (8,75h em decimal). Descontando 1 hora de intervalo: 7h45min trabalhadas (7,75h).
              </p>
            </div>

            <div className="bg-light-bg p-6 rounded-lg">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 2: Turno noturno</h3>
              <p className="text-body-text leading-relaxed">
                Entrada: 22:00. Saída: 06:30 (do dia seguinte).<br/>
                <strong>Resultado:</strong> 8 horas e 30 minutos (8,50h em decimal).
              </p>
            </div>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculadora-de-subtracao-de-horas" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default SubtracaoPage;
